import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useStatisticOptions, useStatsStore } from '../store';

import { useStatisticFilters } from './filters';

import { Order } from '@shared-types/common.types';
import GtmHelper from '@tracking/gtm-helper';

export const useStatisticCategory = () => {
  const category = useStatsStore((state) => state.category);
  const setCategoryAction = useStatsStore((state) => state.setCategory);
  const setSorting = useStatsStore((state) => state.setSorting);
  const setOrder = useStatsStore((state) => state.setOrder);
  const previousCategory = useRef(category);
  const { category: urlCategory } = useParams();
  const { options } = useStatisticOptions();
  const { filters } = useStatisticFilters();
  const navigate = useNavigate();

  const phase = useMemo(() => filters?.phase.toLowerCase(), [filters?.phase]);
  const categories = useMemo(() => Object.keys(options.statTypes[phase] ?? {}), [phase, options.statTypes]);
  const defaultCategory = useMemo(() => categories[0], [categories]);

  const pName = useRef(GtmHelper.getFromDataLayer('pageName') ?? '');

  const trackCategory = useCallback(
    (newCategory: string, oldCategory: string) => {
      let virtPath = `${pName.current}`;

      if (newCategory === defaultCategory) {
        if (oldCategory && virtPath.includes(oldCategory)) {
          virtPath = virtPath.replace(`${oldCategory}/`, '');
        }
      } else if (oldCategory && virtPath.includes(oldCategory)) {
        virtPath = virtPath.replace(oldCategory, newCategory);
      } else {
        virtPath = `${virtPath}${newCategory}/`;
      }

      if (pName.current === virtPath) return;

      pName.current = virtPath;
      GtmHelper.addVirtualPage({ virtPath });
      GtmHelper.addPageMeta({ pageName: virtPath });
    },
    [defaultCategory],
  );

  const setCategory = useCallback(
    (value: string) => {
      if (!categories?.length) return;

      if (!categories.includes(value)) value = defaultCategory;

      if (value !== previousCategory.current) {
        previousCategory.current = value;
        setCategoryAction(value);
        setSorting(null);
        setOrder(Order.DESC);
      }

      if (!options.deepLinkBaseUrl) {
        return;
      }

      if (value === urlCategory) return;
      trackCategory(value, urlCategory ?? defaultCategory);

      if (value === defaultCategory) navigate(`/${location.search}`);
      else navigate(`/${value}/${location.search}`);
    },
    [
      categories,
      defaultCategory,
      options.deepLinkBaseUrl,
      urlCategory,
      trackCategory,
      navigate,
      setCategoryAction,
      setSorting,
      setOrder,
    ],
  );

  useEffect(() => {
    setCategory(urlCategory);
  }, [urlCategory, setCategory]);

  return { categories, category, setCategory };
};
